import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'ps_store_scroll_map';

interface PageState {
  y?: number;                            // #root vertical scrollTop
  c?: Record<string, number>;            // carousel scrollLefts by data-scroll-key
  a?: { id: string; top: number };       // карточка, с которой ушли, и ее позиция на экране
}

function loadFromStorage(): Record<string, PageState> {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return {};
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Позиции живут в sessionStorage, копии карты в памяти нет.
 *
 * Раньше карта держалась в модульной переменной и целиком перезаписывалась при
 * каждом сохранении: стоило модулю переинициализироваться с пустой картой — и
 * первая же запись затирала все сохраненное, восстанавливать было нечего.
 * Теперь каждая запись читает свежую карту, правит одну запись и кладет назад.
 */
function patchEntry(key: string, patch: Partial<PageState>): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const map = loadFromStorage();
    map[key] = { ...(map[key] || {}), ...patch };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function readEntry(key: string): PageState | undefined {
  try {
    return loadFromStorage()[key];
  } catch {
    return undefined;
  }
}

// Скролл сыплет событиями — копим последнее значение и пишем раз в 150 мс.
let pendingY: { key: string; y: number } | null = null;
let pendingCarousels: { key: string; c: Record<string, number> } | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function flushPersist() {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  if (pendingY) {
    patchEntry(pendingY.key, { y: pendingY.y });
    pendingY = null;
  }
  if (pendingCarousels) {
    const { key, c } = pendingCarousels;
    const prev = readEntry(key)?.c || {};
    patchEntry(key, { c: { ...prev, ...c } });
    pendingCarousels = null;
  }
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = setTimeout(flushPersist, 150);
}

function getScrollContainer(): HTMLElement | null {
  return document.getElementById('root');
}

// Путь, на котором мы сейчас находимся. Нужен вне хука — карточка списка
// сообщает сюда, с какого товара уходит.
let activeKey = typeof window !== 'undefined' ? window.location.pathname : '';

/**
 * Запомнить карточку, с которой уходим вглубь, и ее положение на экране.
 * При возврате список прокручивается так, чтобы карточка оказалась там же —
 * это надежнее, чем восстанавливать пиксели: не зависит от того, сколько
 * карточек успело отрисоваться и какой высоты они получились.
 */
export function rememberScrollAnchor(id: string, el?: HTMLElement | null): void {
  try {
    const root = getScrollContainer();
    if (!root) return;
    const node = el || document.querySelector<HTMLElement>(`[data-restore-id="${CSS.escape(id)}"]`);
    flushPersist();
    patchEntry(activeKey, {
      y: root.scrollTop,
      a: node ? { id, top: node.getBoundingClientRect().top } : undefined,
    });
  } catch {
    /* ignore */
  }
}

function setElScrollTopInstant(el: HTMLElement, y: number) {
  try {
    el.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior });
  } catch {
    el.scrollTop = y;
  }
}

function setElScrollLeftInstant(el: HTMLElement, x: number) {
  try {
    el.scrollTo({ left: x, top: 0, behavior: 'instant' as ScrollBehavior });
  } catch {
    el.scrollLeft = x;
  }
}

export const useScrollRestoration = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const currentPath = useRef(pathname);

  // Disable browser-native restoration; flush on tab hide
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.addEventListener('pagehide', flushPersist);
    window.addEventListener('visibilitychange', flushPersist);
    return () => {
      window.removeEventListener('pagehide', flushPersist);
      window.removeEventListener('visibilitychange', flushPersist);
    };
  }, []);

  // Save vertical scroll of #root on every event
  useEffect(() => {
    const el = getScrollContainer();
    if (!el) return;

    const onScroll = () => {
      pendingY = { key: currentPath.current, y: el.scrollTop };
      schedulePersist();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Save horizontal scroll of every carousel — capture phase on document
  // (scroll events do not bubble)
  useEffect(() => {
    const onAnyScroll = (e: Event) => {
      const target = e.target as HTMLElement | Document | null;
      if (!target || target === document) return;
      const el = target as HTMLElement;
      if (typeof el.dataset === 'undefined') return;
      const k = el.dataset.scrollKey;
      if (!k) return;
      const key = currentPath.current;
      if (!pendingCarousels || pendingCarousels.key !== key) pendingCarousels = { key, c: {} };
      pendingCarousels.c[k] = el.scrollLeft;
      schedulePersist();
    };
    document.addEventListener('scroll', onAnyScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', onAnyScroll, true);
  }, []);

  // On route change: restore on POP (back/forward), scroll to top on PUSH/REPLACE
  useEffect(() => {
    const el = getScrollContainer();
    if (!el) return;

    currentPath.current = pathname;
    activeKey = pathname;

    if (navType === 'POP') {
      const state = readEntry(pathname);
      const savedY = state?.y || 0;
      const savedCarousels = state?.c || {};
      const savedAnchor = state?.a;
      const hasAny =
        savedY > 0 || !!savedAnchor || Object.values(savedCarousels).some((v) => v > 0);

      if (hasAny) {
        let cancelled = false;
        // Поправку делаем каждый кадр отрисовки, а не по таймеру: rAF выполняется
        // ДО отрисовки кадра, поэтому промежуточные положения не видны. По таймеру
        // между попытками успевало нарисоваться несколько кадров сверху списка —
        // это и читалось как прыжок.
        const deadline = Date.now() + 8000;
        // Возврат с товара почти всегда происходит с пальцем на экране (свайп,
        // инерция списка). Раньше любой такой touchmove мгновенно отменял
        // восстановление. Первые полсекунды касания игнорируем.
        const ignoreUserUntil = Date.now() + 500;

        const onUserScroll = () => {
          if (Date.now() < ignoreUserUntil) return;
          cancelled = true;
          el.removeEventListener('wheel', onUserScroll);
          el.removeEventListener('touchmove', onUserScroll);
        };
        el.addEventListener('wheel', onUserScroll, { passive: true });
        el.addEventListener('touchmove', onUserScroll, { passive: true });

        const finish = () => {
          cancelled = true;
          el.removeEventListener('wheel', onUserScroll);
          el.removeEventListener('touchmove', onUserScroll);
        };

        const tryRestore = () => {
          if (cancelled) return;

          // Vertical — грубое приближение: подтягивает список к нужному месту и
          // заставляет догрузиться тому, что подгружается по мере прокрутки.
          if (savedY > 0) setElScrollTopInstant(el, savedY);

          // Якорь — точная посадка: ставим ту же карточку на то же место экрана.
          // Переживает случай, когда список отрисовался другой длины, а пиксельная
          // позиция в таком случае прижимается выше.
          if (savedAnchor) {
            const node = document.querySelector<HTMLElement>(
              `[data-restore-id="${CSS.escape(savedAnchor.id)}"]`,
            );
            if (node) {
              const delta = node.getBoundingClientRect().top - savedAnchor.top;
              if (Math.abs(delta) > 2) setElScrollTopInstant(el, el.scrollTop + delta);
              finish();
              return;
            }
          }

          // Carousels
          const all = document.querySelectorAll<HTMLElement>('[data-scroll-key]');
          let allCarouselsDone = true;
          all.forEach((carEl) => {
            const k = carEl.dataset.scrollKey;
            if (!k) return;
            const target = savedCarousels[k];
            if (target == null || target <= 0) return;
            if (Math.abs(carEl.scrollLeft - target) > 2) {
              setElScrollLeftInstant(carEl, target);
              if (Math.abs(carEl.scrollLeft - target) > 2) allCarouselsDone = false;
            }
          });

          const verticalDone = savedY <= 0 || Math.abs(el.scrollTop - savedY) <= 2;

          // Если карточку еще ждем — не заканчиваем, даже когда пиксели совпали:
          // именно она задает точное место, а пиксели могли прижаться выше.
          if (!savedAnchor && verticalDone && allCarouselsDone) {
            finish();
            return;
          }
          if (Date.now() > deadline) {
            finish();
            return;
          }
          requestAnimationFrame(tryRestore);
        };

        tryRestore();

        return () => {
          cancelled = true;
          el.removeEventListener('wheel', onUserScroll);
          el.removeEventListener('touchmove', onUserScroll);
        };
      }
    }

    // PUSH/REPLACE → scroll to top
    el.scrollTo(0, 0);
  }, [pathname, navType]);
};
